/**
 * Weakness-driven "Recommended for you" reading — ranks domains by readiness
 * (weakest first) among those with unread articles, and names the concrete
 * next article to read in each.
 *
 * Readiness convention PINNED to guidance's `readinessForDomain`: a domain
 * absent from `topic_progress` reads as 0 (weakest) — an untouched domain is
 * exactly the one to recommend. Golden tests keep the two from drifting.
 *
 * Pure: consumes `buildReadingPaths` output plus the already-fetched
 * `profile.readiness_scores.topic_progress` array.
 */

/** readiness_score for a domain id from topic_progress, or 0 if absent —
 *  same convention as guidance/nextStep.js `readinessForDomain`. */
function readinessForDomain(topicProgress, domainId) {
  const tp = (topicProgress ?? []).find((t) => String(t?.domain_id) === String(domainId));
  return tp?.readiness_score ?? 0;
}

/**
 * Rank domains for "Recommended for you".
 *
 * Eligible: domains with ≥1 unread article. Sorted ascending by readiness;
 * within equal readiness an in-progress domain (live resume pointer on an
 * unread article) wins; remaining ties keep domainList order (stable sort).
 *
 * The recommended article is the resume pointer when in progress, else the
 * first unread in path order.
 *
 * @param {Object} params
 * @param {Array}  params.paths from buildReadingPaths (domainList order)
 * @param {Array}  params.topicProgress profile.readiness_scores.topic_progress
 * @param {number} [params.limit=3]
 * @returns {Array<{ domainId, domainName, readinessScore, completionPct,
 *                   nextArticleId, nextArticleTitle, nextArticleMinutes,
 *                   reason: 'weakest_domain'|'in_progress' }>}
 */
export function rankReadingRecommendations({ paths, topicProgress, limit = 3 }) {
  const candidates = (paths ?? [])
    .filter((p) => p.total > 0 && p.readCount < p.total)
    .map((p, order) => {
      const resumeItem =
        p.resumeArticleId != null
          ? p.items.find((i) => String(i.id) === String(p.resumeArticleId) && !i.read) ?? null
          : null;
      const nextItem = resumeItem ?? p.items.find((i) => !i.read) ?? null;
      return {
        order,
        inProgress: resumeItem != null,
        readinessScore: readinessForDomain(topicProgress, p.domainId),
        path: p,
        nextItem,
      };
    })
    .filter((c) => c.nextItem != null);

  candidates.sort((a, b) => {
    if (a.readinessScore !== b.readinessScore) return a.readinessScore - b.readinessScore;
    if (a.inProgress !== b.inProgress) return a.inProgress ? -1 : 1;
    return a.order - b.order;
  });

  return candidates.slice(0, limit).map((c) => ({
    domainId: c.path.domainId,
    domainName: c.path.domainName,
    readinessScore: c.readinessScore,
    completionPct: c.path.completionPct,
    nextArticleId: c.nextItem.id,
    nextArticleTitle: c.nextItem.title,
    nextArticleMinutes: c.nextItem.minutes,
    reason: c.inProgress ? 'in_progress' : 'weakest_domain',
  }));
}

/**
 * The single "Recommended for you" hero (and the Home-nudge fallback target).
 *
 * @param {Object} params { paths, topicProgress }
 * @returns {Object|null} rankReadingRecommendations(...)[0] ?? null
 */
export function getRecommendedNextRead({ paths, topicProgress }) {
  return rankReadingRecommendations({ paths, topicProgress, limit: 1 })[0] ?? null;
}

/**
 * Select the landing's reading cards: the Continue-reading hero (the library
 * resume target) and the "Recommended for you" entry, with the suppression
 * rule pinned here so web and mobile can't drift:
 *   - a card only renders if its article is actually openable (the app decides
 *     openability — legacy free slice vs metering vs premium — via callback);
 *   - the recommendation is suppressed when it points at the hero's article
 *     (an in-progress weakest domain would duplicate the hero).
 *
 * @param {Object} params
 * @param {Object|null} params.resume         buildLibrarySummary(paths).resume
 * @param {Object|null} params.recommendation rankReadingRecommendations(...)[0]
 * @param {(domainId: any, articleId: any) => boolean} params.isOpenable
 * @returns {{ hero: Object|null, recommendation: Object|null }}
 */
export function selectLandingReading({ resume, recommendation, isOpenable }) {
  const canOpen = typeof isOpenable === 'function' ? isOpenable : () => true;

  const hero = resume && canOpen(resume.domainId, resume.articleId) ? resume : null;

  const rec =
    recommendation &&
    String(recommendation.nextArticleId) !== String(hero?.articleId ?? '') &&
    canOpen(recommendation.domainId, recommendation.nextArticleId)
      ? recommendation
      : null;

  return { hero, recommendation: rec };
}
